import React, { FC } from 'react'
import RoutingGuideIcon from 'transfix-icons/build/RoutingGuideIcon'
import StarIcon from 'transfix-icons/build/StarIcon'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { INavRouteGroup, INavRoute, IUserPermissions } from 'types'
import {
  ShipmentsIcon,
  LocationPinIcon,
  UserIcon,
  SettingsIcon,
  SearchIcon,
  HomeIcon,
  PeopleIcon,
  TrucksIcon,
  CalendarIcon,
  InfoIcon,
  LogOutIcon,
  HelpIcon,
} from 'transfix-icons'
import { filterRoutes } from 'utils/generateAppDefaults'
import { getUserCookieValues } from 'utils/userAuth'
import { userGroupBooleans } from 'utils/userTypes'
import { USER_TYPE } from 'constants/userTypes'

interface IRoutesConfig {
  primary: INavRoute[]
  secondary?: INavRoute[]
  fleet?: INavRoute[]
  account?: INavRoute[]
}

const CURRENT_YEAR = new Date().getFullYear()

interface IRouteConfigContext {
  /**
   * This determines whether to show a <Tag /> element in sidebar
   * @default false
   */
  hasUserCompletedKeepTruckinIntegration?: boolean
  onLogOut?: () => void
  onToggleModal?: (keyName: 'support' | 'featureFlags', value?: boolean) => void
  /**
   * Is `dedicatedLanesPage` flag enabled
   * @default false
   */
  isDedicatedLanesPageEnabled?: boolean
  /**
   * Is `Carrier Scorecard Page` flag enabled
   * @default false
   */
  showCarrierScorecardPage?: boolean
  /**
   * Is `Equipment Listing Page` flag enabled
   * @default false
   */
  isEquipmentListingPageEnabled?: boolean
  /**
   * Is `Carriers Page` flag enabled
   * @deault false
   */
  isCarriersPageEnabled?: boolean
}

/**
 * Returns a routes config object based on auth and user permissions
 */
export const generateFleetPlannerRoutes = (
  permissions: IUserPermissions | null,
  context: IRouteConfigContext = {}
): IRoutesConfig => {
  const { authToken, userType } = getUserCookieValues()
  const { isAdmin } = userGroupBooleans(userType as USER_TYPE)
  const {
    onLogOut = () => {},
    onToggleModal = () => {},
    isDedicatedLanesPageEnabled = false,
    showCarrierScorecardPage = false,
    isEquipmentListingPageEnabled = false,
    isCarriersPageEnabled = false,
  } = context || {}

  if (!authToken || !permissions) {
    return {
      primary: filterRoutes(
        [
          {
            location: '/login',
            name: 'Log In',
            IconEl: UserIcon as FC,
            isStark: false,
            display: true,
          },
        ],
        {
          excludeBase: true,
        }
      ),
    }
  }

  const accountRoutes: INavRoute[] = [
    {
      location: '/carrier-scorecard',
      name: 'My Rating',
      IconEl: StarIcon as FC,
      display: showCarrierScorecardPage,
    },
    {
      name: 'Support',
      IconEl: HelpIcon as FC,
      display: true,
      onClick: () => {
        // TODO: Move this event handler to it's own handler definition
        // trackEvent('VIEWED_SUPPORT_MODAL')
        onToggleModal('support')
      },
    },
    {
      location: '/user/settings',
      name: 'Settings',
      IconEl: SettingsIcon as FC,
      display: true,
    },
    {
      name: 'Log Out',
      IconEl: LogOutIcon as FC,
      display: true,
      onClick: () => onLogOut(),
    },
  ]

  const primaryRoutes = [
    {
      location: '/home',
      name: 'Home',
      IconEl: HomeIcon as FC,
      display: true,
    },
    {
      location: '/book',
      name: 'Find Loads',
      IconEl: SearchIcon as FC,
      display: true,
    },
    {
      location: '/loads',
      name: 'My Loads',
      IconEl: ShipmentsIcon as FC,
      display: true,
    },
    // https://transfix.atlassian.net/browse/FP-3200
    {
      location: '/dedicated-lanes',
      name: 'Dedicated Lanes',
      IconEl: RoutingGuideIcon as FC,
      display: isDedicatedLanesPageEnabled,
    },
  ]

  const fleetRoutes: INavRoute[] = [
    {
      location: '/schedule',
      name: 'Schedule',
      IconEl: CalendarIcon as FC,
      display: true,
    },
    {
      location: '/team',
      name: 'Team',
      IconEl: PeopleIcon as FC,
      display: true,
    },
    {
      location: '/carriers',
      name: 'Carriers',
      IconEl: TrucksIcon as FC,
      display: isCarriersPageEnabled,
    },
    {
      location: '/facilities',
      name: 'Facilities',
      IconEl: LocationPinIcon as FC,
      display: true,
    },
    {
      location: '/equipment',
      name: 'Equipment',
      IconEl: TrucksIcon as FC,
      display: isEquipmentListingPageEnabled,
      tag: <Tag label='Beta' tagStyle={TAG_STYLE.info} size={TAG_SIZE.xsmall} />,
    },
  ]

  /**
   * Add feature flag modal toggle for admin users
   */
  if (isAdmin) {
    accountRoutes.push({
      name: `© ${CURRENT_YEAR} Transfix, Inc.`,
      IconEl: InfoIcon as FC,
      display: true,
      onClick: () => onToggleModal('featureFlags'),
    })
  }

  return {
    primary: filterRoutes(
      primaryRoutes.filter(({ display }) => display),
      {
        excludeBase: true,
      }
    ),
    secondary: [],
    fleet: filterRoutes(
      fleetRoutes.filter(item => {
        const { display } = item || {}
        return display
      }),
      {
        excludeBase: true,
      }
    ),
    account: filterRoutes(
      accountRoutes.filter(({ display }) => display),
      {
        excludeBase: true,
      }
    ),
  }
}

export interface IGenerateNavRouteGroupsProps {
  permissions: IUserPermissions | null
  /**
   * Adds logic and context, such as auth and custom callbacks
   */
  context?: IRouteConfigContext
}

/**
 * Returns array of route group configs
 */
export const generateNavRouteGroups = ({
  permissions,
  context = {},
}: IGenerateNavRouteGroupsProps): INavRouteGroup[] => {
  const { primary = [], fleet = [], account = [] } = generateFleetPlannerRoutes(
    permissions,
    context
  )
  return (
    [
      {
        keyName: 'primary',
        routes: primary,
        order: 1,
      },
      {
        name: 'Fleet Management',
        keyName: 'fleet',
        routes: fleet,
        dividerBefore: true,
        order: 2,
      },
      {
        name: 'Your Account',
        keyName: 'account',
        routes: account,
        dividerBefore: true,
        order: 3,
      },
    ]
      /**
       * Remove groups with no routes
       */
      .filter(group => {
        const { routes = [] } = group || {}
        return routes.length > 0
      })
      /**
       * Sort groups by `order` property
       */
      .sort((a, b) => {
        const { order: orderA = 0 } = a || {}
        const { order: orderB = 0 } = b || {}
        return orderA - orderB
      })
  )
}
